import { useState, useEffect } from "react";
import { useApp } from "@/App";
import { useTeacherTheme } from "@/contexts/TeacherThemeContext";
import { Mail, School, Shield, ExternalLink, MessageCircle, Lock, HelpCircle, Bell, Smartphone, Monitor, ChevronRight, Moon, Sun } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { monitorService } from "@/services/monitorService";
import { dashboardService } from "@/services/dashboardService";

interface ProfileProps {
  darkMode?: boolean;
  onToggleDarkMode?: () => void;
}

export function Profile({ darkMode = false, onToggleDarkMode }: ProfileProps) {
  const { user } = useApp();
  const { dm } = useTeacherTheme();
  const [schoolName, setSchoolName] = useState<string | null>(null);
  const [keywords, setKeywords] = useState('');

  useEffect(() => {
    dashboardService.getMySchool()
      .then(s => setSchoolName(s.name))
      .catch(() => setSchoolName(null));
  }, []);
  const [syncStatus, setSyncStatus] = useState<'idle' | 'syncing' | 'done' | 'error'>('idle');
  const [notifPrefs, setNotifPrefs] = useState({
    emailAlerts: true,
    browserPush: true,
    smsAlerts: false,
    weeklyDigest: true,
  });

  const userInitials = (user?.name || 'T').split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);

  const toggle = (key: keyof typeof notifPrefs) => {
    setNotifPrefs(prev => ({ ...prev, [key]: !prev[key] }));
  };

  const openUrl = (url: string) => window.open(url, '_blank', 'noopener,noreferrer');

  const handleSyncKeywords = async () => {
    setSyncStatus('syncing');
    try {
      await monitorService.reloadKeywords();
      setSyncStatus('done');
      setTimeout(() => setSyncStatus('idle'), 3000);
    } catch {
      setSyncStatus('error');
      setTimeout(() => setSyncStatus('idle'), 3000);
    }
  };

  return (
    <div className="max-w-7xl mx-auto space-y-6">

      {/* Profile Header */}
      <Card className={`border-0 shadow-sm ${dm ? 'bg-slate-800' : 'bg-white'}`}>
        <CardContent className="p-6">
          <div className="flex items-center gap-5">
            {/* Teacher avatar or initials */}
            <div className="h-16 w-16 rounded-full bg-blue-600 flex items-center justify-center text-white text-xl font-bold shrink-0">
              {userInitials}
            </div>
            <div>
              <h1 className={`text-xl font-bold ${dm ? 'text-slate-100' : 'text-slate-800'}`}>{user?.name || 'Teacher'}</h1>
              <p className={`text-sm ${dm ? 'text-slate-400' : 'text-slate-500'}`}>{user?.email || ''}</p>
              <span className={`inline-block mt-1.5 px-2.5 py-0.5 text-xs font-semibold rounded-full capitalize ${dm ? 'bg-blue-900/40 text-blue-300' : 'bg-blue-100 text-blue-700'}`}>
                {user?.role || 'teacher'}
              </span>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Personal Information */}
      <Card className={`border-0 shadow-sm ${dm ? 'bg-slate-800' : 'bg-white'}`}>
        <CardHeader className="pb-3">
          <CardTitle className={`text-base font-semibold ${dm ? 'text-slate-100' : 'text-slate-800'}`}>Personal Information</CardTitle>
        </CardHeader>
        <CardContent className="pt-0 space-y-4">
          <div className="flex items-center gap-3">
            <div className={`p-2 rounded-lg shrink-0 ${dm ? 'bg-blue-900/40' : 'bg-blue-50'}`}>
              <Mail className={`h-4 w-4 ${dm ? 'text-blue-400' : 'text-blue-600'}`} />
            </div>
            <div>
              <p className={`text-xs ${dm ? 'text-slate-500' : 'text-slate-400'}`}>Email</p>
              <p className={`text-sm ${dm ? 'text-slate-200' : 'text-slate-700'}`}>{user?.email || 'Not set'}</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <div className={`p-2 rounded-lg shrink-0 ${dm ? 'bg-emerald-900/40' : 'bg-emerald-50'}`}>
              <School className={`h-4 w-4 ${dm ? 'text-emerald-400' : 'text-emerald-600'}`} />
            </div>
            <div>
              <p className={`text-xs ${dm ? 'text-slate-500' : 'text-slate-400'}`}>School</p>
              <p className={`text-sm ${dm ? 'text-slate-200' : 'text-slate-700'}`}>{schoolName ?? (user?.schoolId ? 'Loading…' : 'Not assigned')}</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <div className={`p-2 rounded-lg shrink-0 ${dm ? 'bg-purple-900/40' : 'bg-purple-50'}`}>
              <Shield className={`h-4 w-4 ${dm ? 'text-purple-400' : 'text-purple-600'}`} />
            </div>
            <div>
              <p className={`text-xs ${dm ? 'text-slate-500' : 'text-slate-400'}`}>Role</p>
              <p className={`text-sm ${dm ? 'text-slate-200' : 'text-slate-700'} capitalize`}>{user?.role || 'Teacher'}</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Notification Preferences */}
      <Card className={`border-0 shadow-sm ${dm ? 'bg-slate-800' : 'bg-white'}`}>
        <CardHeader className="pb-3">
          <CardTitle className={`text-base font-semibold ${dm ? 'text-slate-100' : 'text-slate-800'}`}>Notification Preferences</CardTitle>
        </CardHeader>
        <CardContent className="pt-0 space-y-1">
          {([
            { key: 'emailAlerts',   icon: Mail,        label: 'Email alerts',          sub: 'Receive alert emails for new incidents',      color: 'text-blue-600' },
            { key: 'browserPush',   icon: Monitor,     label: 'Browser notifications',  sub: 'Push notifications in this browser',       color: 'text-purple-600' },
            { key: 'smsAlerts',     icon: Smartphone,  label: 'SMS alerts',             sub: 'Text message for critical incidents',      color: 'text-emerald-600' },
            { key: 'weeklyDigest',  icon: Bell,        label: 'Weekly digest',          sub: 'Summary email every Monday morning',       color: 'text-amber-600' },
          ] as const).map(({ key, icon: Icon, label, sub, color }) => (
            <div key={key} className={`flex items-center justify-between p-3 rounded-lg transition ${dm ? 'hover:bg-slate-700/60' : 'hover:bg-slate-50'}`}>
              <div className="flex items-center gap-3">
                <div className={`p-2 rounded-lg shrink-0 ${dm ? 'bg-slate-700' : 'bg-slate-100'}`}>
                  <Icon className={`h-4 w-4 ${color}`} />
                </div>
                <div>
                  <p className={`text-sm font-medium ${dm ? 'text-slate-200' : 'text-slate-700'}`}>{label}</p>
                  <p className={`text-xs ${dm ? 'text-slate-500' : 'text-slate-400'}`}>{sub}</p>
                </div>
              </div>
              {/* Toggle switch */}
              <button
                role="switch"
                aria-checked={notifPrefs[key]}
                onClick={() => toggle(key)}
                style={{
                  flexShrink: 0,
                  display: 'inline-flex',
                  alignItems: 'center',
                  width: '2.5rem',
                  height: '1.375rem',
                  borderRadius: '9999px',
                  border: 'none',
                  cursor: 'pointer',
                  padding: 0,
                  transition: 'background-color 0.2s ease',
                  backgroundColor: notifPrefs[key] ? '#3b82f6' : '#cbd5e1',
                  outline: 'none',
                }}
              >
                <span style={{
                  display: 'block',
                  width: '1.125rem',
                  height: '1.125rem',
                  borderRadius: '50%',
                  backgroundColor: 'white',
                  boxShadow: '0 1px 3px rgba(0,0,0,0.25)',
                  transition: 'transform 0.2s ease',
                  transform: notifPrefs[key] ? 'translateX(1.25rem)' : 'translateX(0.125rem)',
                }} />
              </button>
            </div>
          ))}
        </CardContent>
      </Card>

      {/* Appearance */}
      {onToggleDarkMode && (
        <Card className={`border-0 shadow-sm ${dm ? 'bg-slate-800' : 'bg-white'}`}>
          <CardHeader className="pb-3">
            <CardTitle className={`text-base font-semibold ${dm ? 'text-slate-100' : 'text-slate-800'}`}>Appearance</CardTitle>
          </CardHeader>
          <CardContent className="pt-0">
            <div className={`flex items-center justify-between p-3 rounded-lg transition ${dm ? 'hover:bg-slate-700/60' : 'hover:bg-slate-50'}`}>
              <div className="flex items-center gap-3">
                <div className={`p-2 rounded-lg shrink-0 ${dm ? 'bg-slate-700' : 'bg-slate-100'}`}>
                  {darkMode ? <Moon className="h-4 w-4 text-indigo-600" /> : <Sun className="h-4 w-4 text-indigo-600" />}
                </div>
                <div>
                  <p className={`text-sm font-medium ${dm ? 'text-slate-200' : 'text-slate-700'}`}>Dark Mode</p>
                  <p className={`text-xs ${dm ? 'text-slate-500' : 'text-slate-400'}`}>{darkMode ? 'Currently dark' : 'Currently light'}</p>
                </div>
              </div>
              <button
                role="switch"
                aria-checked={darkMode}
                onClick={onToggleDarkMode}
                style={{
                  flexShrink: 0,
                  display: 'inline-flex',
                  alignItems: 'center',
                  width: '2.5rem',
                  height: '1.375rem',
                  borderRadius: '9999px',
                  border: 'none',
                  cursor: 'pointer',
                  padding: 0,
                  transition: 'background-color 0.2s ease',
                  backgroundColor: darkMode ? '#3b82f6' : '#cbd5e1',
                  outline: 'none',
                }}
              >
                <span style={{
                  display: 'block',
                  width: '1.125rem',
                  height: '1.125rem',
                  borderRadius: '50%',
                  backgroundColor: 'white',
                  boxShadow: '0 1px 3px rgba(0,0,0,0.25)',
                  transition: 'transform 0.2s ease',
                  transform: darkMode ? 'translateX(1.25rem)' : 'translateX(0.125rem)',
                }} />
              </button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Keyword Watchlist */}
      <Card className={`border-0 shadow-sm ${dm ? 'bg-slate-800' : 'bg-white'}`}>
        <CardHeader className="pb-3">
          <CardTitle className={`text-base font-semibold ${dm ? 'text-slate-100' : 'text-slate-800'}`}>Keyword Watchlist</CardTitle>
        </CardHeader>
        <CardContent className="pt-0 space-y-3">
          <p className={`text-xs ${dm ? 'text-slate-400' : 'text-slate-500'}`}>
            Add custom keywords or phrases (one per line) to flag in student activity. Changes sync to the detection engine.
          </p>
          <textarea
            value={keywords}
            onChange={e => setKeywords(e.target.value)}
            rows={5}
            placeholder={"harmful phrase\nbullying keyword\nsuicide method"}
            className={`w-full text-sm border rounded-lg p-3 resize-none outline-none focus:ring-2 ${dm ? 'border-slate-700 text-slate-200 placeholder-slate-600 bg-slate-900 focus:ring-blue-500' : 'border-slate-200 text-slate-700 placeholder-slate-300 bg-white focus:ring-blue-200'}`}
          />
          <button
            onClick={handleSyncKeywords}
            disabled={syncStatus === 'syncing'}
            className={`btn-glass btn-glass-pill flex items-center gap-2 disabled:opacity-60 ${
              syncStatus === 'done'  ? 'btn-glass-success' :
              syncStatus === 'error' ? 'btn-glass-danger' :
              'btn-glass-blue'
            }`}
          >
            {syncStatus === 'syncing' ? 'Syncing…' :
             syncStatus === 'done'    ? 'Synced!' :
             syncStatus === 'error'   ? 'Sync failed' :
             'Sync to Detection Engine'}
          </button>
        </CardContent>
      </Card>

      {/* Resources */}
      <Card className={`border-0 shadow-sm ${dm ? 'bg-slate-800' : 'bg-white'}`}>
        <CardHeader className="pb-3">
          <CardTitle className={`text-base font-semibold ${dm ? 'text-slate-100' : 'text-slate-800'}`}>Resources</CardTitle>
        </CardHeader>
        <CardContent className="pt-0 space-y-1">
          <button
            onClick={() => openUrl('mailto:support@shieldedhaven.com?subject=Help%20Request')}
            className={`w-full flex items-center gap-3 p-3 rounded-lg transition-colors text-left ${dm ? 'hover:bg-slate-700/60' : 'hover:bg-slate-50'}`}
            style={{ background: 'none', border: 'none', cursor: 'pointer' }}
          >
            <div className={`p-2 rounded-lg shrink-0 ${dm ? 'bg-blue-900/40' : 'bg-blue-50'}`}>
              <HelpCircle className={`h-4 w-4 ${dm ? 'text-blue-400' : 'text-blue-600'}`} />
            </div>
            <div className="flex-1 min-w-0">
              <p className={`text-sm font-medium ${dm ? 'text-slate-200' : 'text-slate-700'}`}>Help & Support</p>
              <p className={`text-xs ${dm ? 'text-slate-500' : 'text-slate-400'}`}>support@shieldedhaven.com</p>
            </div>
            <ExternalLink className={`h-3.5 w-3.5 shrink-0 ${dm ? 'text-slate-500' : 'text-slate-300'}`} />
          </button>

          <button
            onClick={() => openUrl('mailto:contact@shieldedhaven.com?subject=Teacher%20Inquiry')}
            className={`w-full flex items-center gap-3 p-3 rounded-lg transition-colors text-left ${dm ? 'hover:bg-slate-700/60' : 'hover:bg-slate-50'}`}
            style={{ background: 'none', border: 'none', cursor: 'pointer' }}
          >
            <div className={`p-2 rounded-lg shrink-0 ${dm ? 'bg-emerald-900/40' : 'bg-emerald-50'}`}>
              <MessageCircle className={`h-4 w-4 ${dm ? 'text-emerald-400' : 'text-emerald-600'}`} />
            </div>
            <div className="flex-1 min-w-0">
              <p className={`text-sm font-medium ${dm ? 'text-slate-200' : 'text-slate-700'}`}>Contact Us</p>
              <p className={`text-xs ${dm ? 'text-slate-500' : 'text-slate-400'}`}>contact@shieldedhaven.com</p>
            </div>
            <ExternalLink className={`h-3.5 w-3.5 shrink-0 ${dm ? 'text-slate-500' : 'text-slate-300'}`} />
          </button>

          <button
            onClick={() => openUrl('https://shieldedhaven.com/privacy')}
            className={`w-full flex items-center gap-3 p-3 rounded-lg transition-colors text-left ${dm ? 'hover:bg-slate-700/60' : 'hover:bg-slate-50'}`}
            style={{ background: 'none', border: 'none', cursor: 'pointer' }}
          >
            <div className={`p-2 rounded-lg shrink-0 ${dm ? 'bg-purple-900/40' : 'bg-purple-50'}`}>
              <Lock className={`h-4 w-4 ${dm ? 'text-purple-400' : 'text-purple-600'}`} />
            </div>
            <div className="flex-1 min-w-0">
              <p className={`text-sm font-medium ${dm ? 'text-slate-200' : 'text-slate-700'}`}>Privacy Policy</p>
              <p className={`text-xs ${dm ? 'text-slate-500' : 'text-slate-400'}`}>shieldedhaven.com/privacy</p>
            </div>
            <ChevronRight className={`h-3.5 w-3.5 shrink-0 ${dm ? 'text-slate-500' : 'text-slate-300'}`} />
          </button>
        </CardContent>
      </Card>
    </div>
  );
}
